import * as Data from './../../../../storage/app/countries.json'
import * as Timezones from './../../../../storage/app/timezones.json'
import * as States from './../../../../storage/app/provinces.json'
import { map, filter, sortBy, uniq, reduceRight } from 'lodash'
import { computed } from 'vue'

const _data = Data.default
const _timezones = Timezones.default
const _states = States.default
const _priority = ['US', 'CA', 'GB', 'AU']

const _symbols = {
    AED: 'د.إ',
    AFN: '؋',
    ALL: 'L',
    AMD: '֏',
    ANG: 'ƒ',
    AOA: 'Kz',
    ARS: '$',
    AUD: '$',
    AWG: 'ƒ',
    AZN: '₼',
    BAM: 'KM',
    BBD: '$',
    BDT: '৳',
    BGN: 'лв',
    BHD: '.د.ب',
    BIF: 'FBu',
    BMD: '$',
    BND: '$',
    BOB: '$b',
    BRL: 'R$',
    BSD: '$',
    BTC: '฿',
    BTN: 'Nu.',
    BWP: 'P',
    BYR: 'Br',
    BYN: 'Br',
    BZD: 'BZ$',
    CAD: '$',
    CDF: 'FC',
    CHF: 'CHF',
    CLP: '$',
    CNY: '¥',
    COP: '$',
    CRC: '₡',
    CUC: '$',
    CUP: '₱',
    CVE: '$',
    CZK: 'Kč',
    DJF: 'Fdj',
    DKK: 'kr',
    DOP: 'RD$',
    DZD: 'دج',
    EEK: 'kr',
    EGP: '£',
    ERN: 'Nfk',
    ETB: 'Br',
    ETH: 'Ξ',
    EUR: '€',
    FJD: '$',
    FKP: '£',
    GBP: '£',
    GEL: '₾',
    GGP: '£',
    GHC: '₵',
    GHS: 'GH₵',
    GIP: '£',
    GMD: 'D',
    GNF: 'FG',
    GTQ: 'Q',
    GYD: '$',
    HKD: '$',
    HNL: 'L',
    HRK: 'kn',
    HTG: 'G',
    HUF: 'Ft',
    IDR: 'Rp',
    ILS: '₪',
    IMP: '£',
    INR: '₹',
    IQD: 'ع.د',
    IRR: '﷼',
    ISK: 'kr',
    JEP: '£',
    JMD: 'J$',
    JOD: 'JD',
    JPY: '¥',
    KES: 'KSh',
    KGS: 'лв',
    KHR: '៛',
    KMF: 'CF',
    KPW: '₩',
    KRW: '₩',
    KWD: 'KD',
    KYD: '$',
    KZT: 'лв',
    LAK: '₭',
    LBP: '£',
    LKR: '₨',
    LRD: '$',
    LSL: 'M',
    LTC: 'Ł',
    LTL: 'Lt',
    LVL: 'Ls',
    LYD: 'LD',
    MAD: 'MAD',
    MDL: 'lei',
    MGA: 'Ar',
    MKD: 'ден',
    MMK: 'K',
    MNT: '₮',
    MOP: 'MOP$',
    MRO: 'UM',
    MRU: 'UM',
    MUR: '₨',
    MVR: 'Rf',
    MWK: 'MK',
    MXN: '$',
    MYR: 'RM',
    MZN: 'MT',
    NAD: '$',
    NGN: '₦',
    NIO: 'C$',
    NOK: 'kr',
    NPR: '₨',
    NZD: '$',
    OMR: '﷼',
    PAB: 'B/.',
    PEN: 'S/.',
    PGK: 'K',
    PHP: '₱',
    PKR: '₨',
    PLN: 'zł',
    PYG: 'Gs',
    QAR: '﷼',
    RMB: '￥',
    RON: 'lei',
    RSD: 'Дин.',
    RUB: '₽',
    RWF: 'R₣',
    SAR: '﷼',
    SBD: '$',
    SCR: '₨',
    SDG: 'ج.س.',
    SEK: 'kr',
    SGD: '$',
    SHP: '£',
    SLL: 'Le',
    SOS: 'S',
    SRD: '$',
    SSP: '£',
    STD: 'Db',
    STN: 'Db',
    SVC: '$',
    SYP: '£',
    SZL: 'E',
    THB: '฿',
    TJS: 'SM',
    TMT: 'T',
    TND: 'د.ت',
    TOP: 'T$',
    TRL: '₤',
    TRY: '₺',
    TTD: 'TT$',
    TVD: '$',
    TWD: 'NT$',
    TZS: 'TSh',
    UAH: '₴',
    UGX: 'USh',
    USD: '$',
    UYU: '$U',
    UZS: 'лв',
    VEF: 'Bs',
    VND: '₫',
    VUV: 'VT',
    WST: 'WS$',
    XAF: 'FCFA',
    XBT: 'Ƀ',
    XCD: '$',
    XOF: 'CFA',
    XPF: '₣',
    YER: '﷼',
    ZAR: 'R',
    ZWD: 'Z$'
}

export function useCountries() {
    const data = computed(() => {
        return sortBy(
            sortBy(_data, i => i.name.common),
            i => (_priority.indexOf(i.cca2) === -1 ? _priority.length : _priority.indexOf(i.cca2))
        )
    })

    const callingCodes = computed(() => {
        return map(
            filter(data.value, c => c.callingCode.length > 0),
            i => {
                return { key: i.cca2, name: i.name.common, value: '+' + i.callingCode[0] }
            }
        )
    })

    const locales = computed(() => {
        return map(
            filter(data.value, c => Object.keys(c.languages).length > 0),
            i => {
                return {
                    key: i.cca2,
                    name: i.name.common,
                    value: Object.keys(i.languages)[0].substring(0, 2) + '-' + i.cca2.toUpperCase()
                }
            }
        )
    })

    const currencies = computed(() => {
        var priority = ['USD', 'EUR', 'GBP', 'CAD', 'AUD']
        return sortBy(uniq(reduceRight(_data, (r, i) => r.concat(i.currency), [])).sort(), i =>
            priority.indexOf(i) === -1 ? priority.length : priority.indexOf(i)
        )
    })

    const symbols = computed(() => _symbols)

    const timezones = computed(() => {
        return map(_timezones, (v, k) => ({ key: k, name: v }))
    })

    const countries = computed(() => {
        return map(data.value, i => ({ key: i.cca2, name: i.name.common }))
    })

    const states = key => {
        return map(
            _states.filter(s => s.country == key),
            s => ({ key: s.short ? s.short : s.name, name: s.name })
        )
    }

    return {
        data,
        callingCodes,
        locales,
        currencies,
        symbols,
        timezones,
        countries,
        states
    }
}
